import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Check, Loader2, Pencil, Plus, Tag, Trash2, X } from "lucide-react";
import { useState } from "react";
import { api } from "../lib/api";
import { usePermissoes } from "../lib/permissoes";

interface Props {
  condominioId: number;
  onVoltar: () => void;
}

interface Fornecedor {
  id: number;
  codigo: string;
  nome_exibicao: string;
  categoria: string | null;
  padrao_regex: string;
  ativo: boolean;
}

export function FornecedoresPage({ condominioId, onVoltar }: Props) {
  const { podeEscrever } = usePermissoes();
  const podeEditar = podeEscrever(condominioId, "condominio");
  const qc = useQueryClient();
  const { data, isLoading } = useQuery<Fornecedor[]>({
    queryKey: ["fornecedores", condominioId],
    queryFn: () => api.get<Fornecedor[]>(`/condominios/${condominioId}/fornecedores`),
  });

  const [nome, setNome] = useState("");
  const [categoria, setCategoria] = useState("");
  const [padrao, setPadrao] = useState("");

  const criar = useMutation({
    mutationFn: () =>
      api.post(`/condominios/${condominioId}/fornecedores`, {
        nome_exibicao: nome.trim(),
        categoria: categoria.trim() || null,
        padrao_regex: padrao.trim() || null,
      }),
    onSuccess: () => {
      setNome("");
      setCategoria("");
      setPadrao("");
      qc.invalidateQueries({ queryKey: ["fornecedores", condominioId] });
    },
  });

  const excluir = useMutation({
    mutationFn: (id: number) => api.delete(`/fornecedores/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["fornecedores", condominioId] }),
  });

  // Edição inline (nome / categoria / padrão)
  const [editId, setEditId] = useState<number | null>(null);
  const [eNome, setENome] = useState("");
  const [eCat, setECat] = useState("");
  const [ePad, setEPad] = useState("");
  const abrirEdicao = (f: Fornecedor) => {
    setEditId(f.id);
    setENome(f.nome_exibicao);
    setECat(f.categoria ?? "");
    setEPad(f.padrao_regex);
  };
  const editar = useMutation({
    mutationFn: (id: number) =>
      api.patch(`/fornecedores/${id}`, {
        nome_exibicao: eNome.trim(),
        categoria: eCat.trim() || null,
        padrao_regex: ePad.trim() || undefined,
      }),
    onSuccess: () => {
      setEditId(null);
      qc.invalidateQueries({ queryKey: ["fornecedores", condominioId] });
    },
  });

  const lista = data ?? [];
  const inp = "w-full px-2 py-1.5 border border-slate-300 rounded-md text-sm";

  return (
    <div>
      <button
        type="button"
        onClick={onVoltar}
        className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-slate-900 mb-4"
      >
        <ArrowLeft size={16} />
        Voltar
      </button>

      <div className="flex items-center gap-2 mb-1">
        <Tag size={22} className="text-brand-700" />
        <h2 className="text-2xl font-semibold text-slate-900">Fornecedores do Histórico</h2>
      </div>
      <p className="text-sm text-slate-500 mb-4">
        Serviços/contratos recorrentes acompanhados no <b>Histórico</b> (energia, água, elevador,
        limpeza…). No 1º processamento o sistema sugere os que encontrou; aqui você pode
        <b> adicionar</b>, <b>editar</b> ou <b>excluir</b>.
      </p>

      {/* Dica de separação */}
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 mb-5 text-[12px] text-amber-900">
        <b>Para separar itens do mesmo fornecedor</b> (ex.: elevador <b>manutenção</b> x
        <b> peças</b>): crie um item para cada e preencha <b>“Começa com”</b> = as
        <b> primeiras palavras da linha</b> do balancete. Não use símbolos e não se
        preocupe com acentos ou maiúsculas. Ex.:{" "}
        <code className="bg-white px-1 rounded border border-amber-200">ELEVADOR MANUTENCAO</code> e{" "}
        <code className="bg-white px-1 rounded border border-amber-200">ELEVADOR PECAS</code>.
        Depois <b>reprocesse</b> o mês. (Vazio = casa pelo próprio nome.)
      </div>

      {/* Adicionar */}
      {podeEditar && (
      <div className="bg-white border border-slate-200 rounded-2xl shadow-sm p-4 mb-5">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div>
            <label className="block text-[11px] font-medium text-slate-500 mb-1">
              Nome (como você quer ver)
            </label>
            <input value={nome} onChange={(e) => setNome(e.target.value)}
              placeholder="Ex.: Elevador — Peças" className={inp} />
          </div>
          <div>
            <label className="block text-[11px] font-medium text-slate-500 mb-1">
              Categoria (opcional)
            </label>
            <input value={categoria} onChange={(e) => setCategoria(e.target.value)}
              placeholder="Ex.: Elevadores" className={inp} />
          </div>
          <div>
            <label className="block text-[11px] font-medium text-slate-500 mb-1">
              Começa com (primeiras palavras da linha) — opcional
            </label>
            <input value={padrao} onChange={(e) => setPadrao(e.target.value)}
              placeholder="Ex.: ELEVADOR PECAS" className={inp} />
          </div>
        </div>
        <div className="mt-3 flex items-center gap-3">
          <button type="button" onClick={() => criar.mutate()}
            disabled={!nome.trim() || criar.isPending}
            className="inline-flex items-center gap-1.5 px-3 py-2 bg-brand-700 hover:bg-brand-800 disabled:opacity-50 text-white text-sm font-medium rounded-md">
            {criar.isPending ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
            Adicionar
          </button>
          {criar.isError && (
            <span className="text-xs text-rose-700">Não foi possível adicionar (código duplicado?).</span>
          )}
        </div>
      </div>
      )}

      {/* Lista */}
      <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
        {isLoading ? (
          <div className="p-8 text-center text-slate-400 text-sm">Carregando…</div>
        ) : lista.length === 0 ? (
          <div className="p-8 text-center text-slate-400 text-sm">
            Nenhum fornecedor cadastrado. Processe um mês para o sistema sugerir, ou adicione acima.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 text-slate-500 text-xs uppercase">
                <th className="text-left font-semibold px-4 py-2">Nome</th>
                <th className="text-left font-semibold px-4 py-2">Categoria</th>
                <th className="text-left font-semibold px-4 py-2">Começa com</th>
                <th className="px-4 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {lista.map((f) =>
                editId === f.id ? (
                  <tr key={f.id} className="border-t border-slate-100 bg-brand-50/30">
                    <td className="px-4 py-2"><input value={eNome} onChange={(e) => setENome(e.target.value)} className={inp} /></td>
                    <td className="px-4 py-2"><input value={eCat} onChange={(e) => setECat(e.target.value)} className={inp} /></td>
                    <td className="px-4 py-2"><input value={ePad} onChange={(e) => setEPad(e.target.value)} className={inp} placeholder="ELEVADOR MANUTENCAO" /></td>
                    <td className="px-4 py-2 text-right whitespace-nowrap">
                      <button type="button" onClick={() => editar.mutate(f.id)} disabled={editar.isPending}
                        className="p-1.5 rounded-md text-emerald-600 hover:bg-emerald-50" title="Salvar">
                        {editar.isPending ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} />}
                      </button>
                      <button type="button" onClick={() => setEditId(null)}
                        className="p-1.5 rounded-md text-slate-400 hover:bg-slate-100" title="Cancelar">
                        <X size={16} />
                      </button>
                    </td>
                  </tr>
                ) : (
                  <tr key={f.id} className="border-t border-slate-100">
                    <td className="px-4 py-2 text-slate-800">{f.nome_exibicao}</td>
                    <td className="px-4 py-2 text-slate-500">{f.categoria ?? "—"}</td>
                    <td className="px-4 py-2">
                      <code className="text-[11px] text-slate-500 bg-slate-50 border border-slate-200 rounded px-1">
                        {f.padrao_regex}
                      </code>
                    </td>
                    <td className="px-4 py-2 text-right whitespace-nowrap">
                      {podeEditar && (
                        <>
                          <button type="button" onClick={() => abrirEdicao(f)}
                            className="p-1.5 rounded-md text-slate-400 hover:text-brand-700 hover:bg-brand-50" title="Editar">
                            <Pencil size={15} />
                          </button>
                          <button type="button"
                            onClick={() => { if (window.confirm(`Excluir "${f.nome_exibicao}" do histórico?`)) excluir.mutate(f.id); }}
                            disabled={excluir.isPending}
                            className="p-1.5 rounded-md text-slate-400 hover:text-rose-600 hover:bg-rose-50 disabled:opacity-40" title="Excluir">
                            <Trash2 size={15} />
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                ),
              )}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
